// c:\xampp\htdocs\miners-market-main\react-app\src\pages\UserPathsPage.jsx
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'

const API_BASE = '/api'

function fmtDate(v) {
  if (!v) return '-'
  return new Date(v).toLocaleString('en-ZA', { dateStyle: 'short', timeStyle: 'short' })
}
function fmtShort(v) {
  if (!v) return '-'
  return new Date(v).toLocaleDateString('en-ZA', { dateStyle: 'short' })
}

export default function UserPathsPage({ user }) {
  const navigate = useNavigate()

  const EVENT_LABELS = {
    app_opened: 'App Opened',
    session_started: 'Session Started',
    session_expired: 'Session Expired',
    page_visited: 'Page Visited',
    route_changed: 'Route Changed',
    category_visited: 'Category Visited',
    search_performed: 'Search Performed',
    filters_applied: 'Filters Applied',
    pagination_changed: 'Pagination Changed',
    product_card_clicked: 'Product Card Clicked',
    product_modal_opened: 'Product Modal Opened',
    product_viewed: 'Product Viewed',
    product_gallery_interacted: 'Product Gallery Interacted',
    product_saved: 'Product Saved',
    product_unsaved: 'Product Unsaved',
    product_added_to_basket: 'Product Added to Basket',
    product_quantity_changed: 'Product Quantity Changed',
    product_removed_from_basket: 'Product Removed from Basket',
    basket_opened: 'Basket Opened',
    quote_request_initiated: 'Quote Request Initiated',
    quote_request_submitted: 'Quote Request Submitted',
  }

  function formatEventLabel(eventType) {
    if (eventType === 0 || eventType === '0') {
      return 'Unknown Activity (Legacy Event)'
    }
    const key = String(eventType)
    return EVENT_LABELS[key] || key
      .split('_')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ')
  }

  function formatSizes(sizes) {
    if (!sizes || typeof sizes !== 'object') return ''
    const entries = Object.entries(sizes)
    if (entries.length === 0) return ''
    return entries.map(([s, q]) => `${s}×${q}`).join(', ')
  }

  function formatEventInitials(eventType) {
    const key = String(eventType || '')
    if (key === '0') return 'UNK'
    return key
      .split('_')
      .map((w) => w.charAt(0))
      .join('')
      .toUpperCase()
  }

  // --- Filters ---
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 6); return d.toISOString().split('T')[0]
  })
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().split('T')[0])
  const [visitorType, setVisitorType] = useState('all')
  const [userName, setUserName] = useState('')
  const [visitorId, setVisitorId] = useState('')
  const [productName, setProductName] = useState('')
  const [category, setCategory] = useState('')
  const [activityType, setActivityType] = useState('')
  const [basketOnly, setBasketOnly] = useState(false)
  const [savedOnly, setSavedOnly] = useState(false)
  const [sort, setSort] = useState('recent')
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(10)
  const [allTime, setAllTime] = useState(false)

  // --- Data ---
  const [visitors, setVisitors] = useState([])
  const [summary, setSummary] = useState({})
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total_records: 0, total_pages: 1 })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  // --- Detail view ---
  const [detailVisitor, setDetailVisitor] = useState(null)
  const [detailSummary, setDetailSummary] = useState(null)
  const [detailProducts, setDetailProducts] = useState([])
  const [detailSessions, setDetailSessions] = useState([])
  const [events, setEvents] = useState([])
  const [eventsPagination, setEventsPagination] = useState({ page: 1, limit: 10, total_records: 0, total_pages: 1 })
  const [detailLoading, setDetailLoading] = useState(false)
  const [selectedSessionId, setSelectedSessionId] = useState(null)
  const [detailEventTypeFilter, setDetailEventTypeFilter] = useState('')
  const [detailOpen, setDetailOpen] = useState(false)
  const detailRequestIdRef = useRef(0)

  // --- Actions ---
  const [deletingId, setDeletingId] = useState(null)
  const [deletingUuid, setDeletingUuid] = useState(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [message, setMessage] = useState({ type: '', text: '' })

  const qs = useCallback((extra = {}) => {
    const p = new URLSearchParams()
    if (!allTime) {
      p.set('date_from', dateFrom)
      p.set('date_to', dateTo)
    } else {
      p.set('all_time', '1')
    }
    p.set('visitor_type', visitorType)
    p.set('user_name', userName)
    p.set('visitor_id', visitorId)
    p.set('product_name', productName)
    p.set('category', category)
    p.set('activity_type', activityType)
    p.set('basket_activity', basketOnly ? '1' : '0')
    p.set('saved_activity', savedOnly ? '1' : '0')
    p.set('sort', sort)
    p.set('page', page)
    p.set('limit', limit)
    Object.entries(extra).forEach(([k, v]) => p.set(k, String(v)))
    return p.toString()
  }, [dateFrom, dateTo, allTime, visitorType, userName, visitorId, productName, category, activityType, basketOnly, savedOnly, sort, page, limit])

  const fetchData = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      const res = await fetch(`${API_BASE}/admin_user_paths.php?${qs()}`, { credentials: 'include' })
      if (!res.ok) throw new Error('Failed to load visitors')
      const data = await res.json()
      if (!data.success) throw new Error(data.message || 'Failed')
      setVisitors(data.visitors || [])
      setSummary(data.summary || {})
      setPagination(data.pagination || { page: 1, limit: 10, total_records: 0, total_pages: 1 })
    } catch (e) { setError(e.message); console.error(e) }
    finally { setLoading(false) }
  }, [qs])

  useEffect(() => { fetchData() }, [fetchData])

  const quickRange = useCallback((label) => {
    if (label === 'All Time') {
      setAllTime(true)
      setPage(1)
      return
    }
    setAllTime(false)
    const d = new Date(); const to = new Date()
    if (label === 'Today') { d.setHours(0,0,0,0) }
    else if (label === 'Yesterday') { d.setDate(d.getDate()-1); d.setHours(0,0,0,0); to.setDate(to.getDate()-1); to.setHours(23,59,59,999) }
    else if (label === 'Last 7 Days') { d.setDate(d.getDate()-6) }
    else if (label === 'Last 30 Days') { d.setDate(d.getDate()-29) }
    else if (label === 'This Month') { d.setDate(1) }
    else if (label === 'Last Month') { d.setMonth(d.getMonth()-1,1); to.setMonth(to.getMonth(),0) }
    setDateFrom(d.toISOString().split('T')[0])
    setDateTo(to.toISOString().split('T')[0])
    setPage(1)
  }, [])

  const loadDetail = useCallback(async (visitor) => {
    if (!visitor) return
    const requestId = Date.now()
    detailRequestIdRef.current = requestId
    // Clear previous visitor data immediately to prevent cross-visitor display
    setDetailVisitor(visitor)
    setDetailSummary(null)
    setDetailProducts([])
    setDetailSessions([])
    setEvents([])
    setEventsPagination({ page: 1, limit: 10, total_records: 0, total_pages: 1 })
    setDetailLoading(true)
    try {
      const dRes = await fetch(`${API_BASE}/admin_user_path_details.php?visitor_id=${visitor.visitor_id}&date_from=${dateFrom}&date_to=${dateTo}`, { credentials: 'include' })
      if (detailRequestIdRef.current !== requestId) return
      const dData = await dRes.json()
      if (detailRequestIdRef.current !== requestId) return
      if (dData.success) {
        setDetailSummary(dData.summary || {})
        setDetailProducts(dData.products || [])
        setDetailSessions(dData.sessions || [])
      }
      const eRes = await fetch(`${API_BASE}/admin_user_path_events.php?visitor_id=${visitor.visitor_id}&date_from=${dateFrom}&date_to=${dateTo}&page=1&limit=${eventsPagination.limit || 10}`, { credentials: 'include' })
      if (detailRequestIdRef.current !== requestId) return
      const eData = await eRes.json()
      if (detailRequestIdRef.current !== requestId) return
      if (eData.success) {
        setEvents(eData.events || [])
        setEventsPagination(eData.pagination || { page: 1, limit: 10, total_records: 0, total_pages: 1 })
      }
    } catch (e) {
      if (detailRequestIdRef.current === requestId) console.error(e)
    }
    finally {
      if (detailRequestIdRef.current === requestId) setDetailLoading(false)
    }
  }, [dateFrom, dateTo, eventsPagination.limit])

  const openDetail = useCallback((v) => {
    loadDetail(v)
    setDetailOpen(true)
  }, [loadDetail])

  const deleteVisitor = useCallback(async () => {
    if (!deletingId) return
    try {
      const res = await fetch(`${API_BASE}/admin_user_path_delete.php`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ visitor_id: deletingId }),
      })
      const data = await res.json()
      if (data.success) {
        setMessage({ type: 'success', text: 'Visitor history deleted.' })
        setShowDeleteConfirm(false)
        setDeletingId(null); setDeletingUuid(null)
        fetchData()
        if (detailOpen) setDetailOpen(false)
      } else {
        setMessage({ type: 'error', text: data.message || 'Delete failed.' })
      }
    } catch (e) { setMessage({ type: 'error', text: e.message }) }
  }, [deletingId, detailOpen, fetchData])

  const exportCsv = useCallback(() => {
    const url = `${API_BASE}/admin_user_paths_export.php?${qs({ format: 'csv' })}`
    window.location.href = url
  }, [qs])

  const exportXlsx = useCallback(() => {
    const url = `${API_BASE}/admin_user_paths_export.php?${qs({ format: 'xlsx' })}`
    window.location.href = url
  }, [qs])

  // --- Detail events pagination ---
  const loadDetailEvents = useCallback((pg) => {
    if (!detailVisitor) return
    const requestId = Date.now()
    detailRequestIdRef.current = requestId
    setDetailLoading(true)
    fetch(`${API_BASE}/admin_user_path_events.php?visitor_id=${detailVisitor.visitor_id}&date_from=${dateFrom}&date_to=${dateTo}&event_type=${detailEventTypeFilter}&page=${pg}&limit=${eventsPagination.limit || 10}`, { credentials: 'include' })
      .then(r => r.json()).then(d => {
        if (detailRequestIdRef.current !== requestId) return
        if (d.success) { setEvents(d.events || []); setEventsPagination(d.pagination || { page: 1, limit: 10, total_records: 0, total_pages: 1 }) }
      }).finally(() => { if (detailRequestIdRef.current === requestId) setDetailLoading(false) })
  }, [detailVisitor, dateFrom, dateTo, detailEventTypeFilter, eventsPagination.limit])

  // --- Summary cards ---
  const cards = useMemo(() => [
    { label: 'Unique Visitors', value: summary.unique_visitors ?? 0, color: 'blue' },
    { label: 'Anonymous Visitors', value: summary.anonymous_visitors ?? 0, color: 'gray' },
    { label: 'Registered Visitors', value: summary.registered_visitors ?? 0, color: 'green' },
    { label: 'Active Sessions', value: summary.active_sessions ?? 0, color: 'indigo' },
    { label: 'Total Page Views', value: summary.total_page_views ?? 0, color: 'purple' },
    { label: 'Product Views', value: summary.product_views ?? 0, color: 'pink' },
    { label: 'Basket Additions', value: summary.basket_additions ?? 0, color: 'orange' },
    { label: 'Saved Product Events', value: summary.saved_product_events ?? 0, color: 'red' },
  ], [summary])

  // --- Render ---
  const handleDeleteHistory = () => {
    setDeletingId(detailVisitor.visitor_id);
    setDeletingUuid(detailVisitor.visitor_uuid);
    setShowDeleteConfirm(true);
    setDetailOpen(false);
  };

  return (
    <div data-name="user-paths-page" className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">User Paths</h1>
            <p className="text-gray-600">Visitor journey analytics for the marketplace</p>
          </div>
          <div className="flex gap-2">
            <button onClick={exportCsv} className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 flex items-center gap-2">
              <i className="fas fa-file-csv"></i> Export CSV
            </button>
            <button onClick={exportXlsx} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 flex items-center gap-2">
              <i className="fas fa-file-excel"></i> Export Excel
            </button>
            <button onClick={fetchData} className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
              <i className="fas fa-refresh"></i> Refresh
            </button>
          </div>
        </div>

        {message.type && (
          <div className={`mb-4 p-4 rounded-lg text-sm ${message.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
            {message.text}
          </div>
        )}

        {/* Summary cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {cards.map(c => (
            <div key={c.label} className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">{c.label}</p>
              <p className={`text-2xl font-bold mt-1 ${c.color === 'blue' ? 'text-blue-600' : c.color === 'gray' ? 'text-gray-600' : c.color === 'green' ? 'text-green-600' : c.color === 'indigo' ? 'text-indigo-600' : c.color === 'purple' ? 'text-purple-600' : c.color === 'pink' ? 'text-pink-600' : c.color === 'orange' ? 'text-orange-600' : 'text-red-600'}`}>{c.value}</p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-200 mb-6">
          <div className="flex flex-wrap gap-3 mb-4">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-xs font-medium text-gray-700 mb-1">Date From</label>
              <input type="date" value={dateFrom} onChange={e => { setAllTime(false); setDateFrom(e.target.value); setPage(1) }} className="w-full p-2 border border-gray-300 rounded text-sm" />
            </div>
            <div className="flex-1 min-w-[200px]">
              <label className="block text-xs font-medium text-gray-700 mb-1">Date To</label>
              <input type="date" value={dateTo} onChange={e => { setAllTime(false); setDateTo(e.target.value); setPage(1) }} className="w-full p-2 border border-gray-300 rounded text-sm" />
            </div>
            <select value={visitorType} onChange={e => { setVisitorType(e.target.value); setPage(1) }} className="p-2 border border-gray-300 rounded text-sm">
              <option value="all">All Visitors</option><option value="anonymous">Anonymous</option><option value="registered">Registered</option>
            </select>
            <input placeholder="User name" value={userName} onChange={e => { setUserName(e.target.value); setPage(1) }} className="p-2 border border-gray-300 rounded text-sm" />
            <input placeholder="Visitor ID" value={visitorId} onChange={e => { setVisitorId(e.target.value); setPage(1) }} className="p-2 border border-gray-300 rounded text-sm" />
            <input placeholder="Product name" value={productName} onChange={e => { setProductName(e.target.value); setPage(1) }} className="p-2 border border-gray-300 rounded text-sm" />
            <input placeholder="Category" value={category} onChange={e => { setCategory(e.target.value); setPage(1) }} className="p-2 border border-gray-300 rounded text-sm" />
            <select value={activityType} onChange={e => { setActivityType(e.target.value); setPage(1) }} className="p-2 border border-gray-300 rounded text-sm">
              <option value="">Activity Type</option>
              <option value="page_visited">Page Visited</option><option value="product_viewed">Product Viewed</option>
              <option value="product_added_to_basket">Basket Add</option><option value="product_saved">Saved</option>
              <option value="basket_opened">Basket Opened</option><option value="quote_request_submitted">Quote Submitted</option>
            </select>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={basketOnly} onChange={() => { setBasketOnly(!basketOnly); setPage(1) }} /> Basket only
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={savedOnly} onChange={() => { setSavedOnly(!savedOnly); setPage(1) }} /> Saved only
            </label>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => quickRange('All Time')} className={`px-3 py-1.5 rounded text-sm ${allTime ? 'bg-indigo-100 text-indigo-700 font-medium' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'}`}>All Time</button>
            <button onClick={() => quickRange('Last 7 Days')} className="px-3 py-1.5 bg-white border border-gray-300 rounded text-sm text-gray-700 hover:bg-gray-50">Last 7 Days</button>
            <button onClick={() => quickRange('Last 30 Days')} className="px-3 py-1.5 bg-white border border-gray-300 rounded text-sm text-gray-700 hover:bg-gray-50">Last 30 Days</button>
            <button onClick={() => quickRange('This Month')} className="px-3 py-1.5 bg-white border border-gray-300 rounded text-sm text-gray-700 hover:bg-gray-50">This Month</button>
            <select value={sort} onChange={e => { setSort(e.target.value); setPage(1) }} className="p-2 border border-gray-300 rounded text-sm">
              <option value="recent">Sort: Most Recent</option><option value="active">Most Active</option><option value="product">Top Product Engagement</option>
            </select>
          </div>
        </div>

        {/* Desktop list / Mobile cards */}
        {pagination.total_records === 0 ? (
          <div className="bg-white p-12 rounded-xl border border-gray-200 text-center">
            <p className="text-gray-500">No visitors found for the selected filters.</p>
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-gray-50 text-left border-b border-gray-200">
                    <th className="p-3 font-medium text-gray-600">Visitor ID</th>
                    <th className="p-3 font-medium text-gray-600">User</th>
                    <th className="p-3 font-medium text-gray-600">Type</th>
                    <th className="p-3 font-medium text-gray-600">First Visit</th>
                    <th className="p-3 font-medium text-gray-600">Last Active</th>
                    <th className="p-3 font-medium text-gray-600">Sessions</th>
                    <th className="p-3 font-medium text-gray-600">Page Views</th>
                    <th className="p-3 font-medium text-gray-600">Product Views</th>
                    <th className="p-3 font-medium text-gray-600">Basket Adds</th>
                    <th className="p-3 font-medium text-gray-600">Saved Items</th>
                    <th className="p-3 font-medium text-gray-600">Total Events</th>
                    <th className="p-3 font-medium text-gray-600 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visitors.map(v => (
                    <tr key={v.visitor_id} className="border-b border-gray-100 hover:bg-gray-50 group">
                      <td className="p-3 font-mono text-sm text-gray-900">{v.visitor_uuid}</td>
                      <td className="p-3">{v.user_name}</td>
                      <td className="p-3"><span className={`px-2 py-1 rounded text-xs font-medium ${v.visitor_type === 'Registered' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>{v.visitor_type}</span></td>
                      <td className="p-3 text-gray-600">{fmtShort(v.first_seen_at)}</td>
                      <td className="p-3 text-gray-600">{fmtShort(v.last_seen_at)}</td>
                      <td className="p-3 text-gray-600">{v.sessions}</td>
                      <td className="p-3 text-gray-600">{v.page_views}</td>
                      <td className="p-3 text-gray-600">{v.product_views}</td>
                      <td className="p-3 text-gray-600">{v.basket_adds}</td>
                      <td className="p-3 text-gray-600">{v.saved_items}</td>
                      <td className="p-3 font-medium text-gray-900">{v.total_events}</td>
                      <td className="p-3 text-right">
                        <button onClick={() => openDetail(v)} className="text-blue-600 hover:text-blue-800 text-sm font-medium mr-3">View Details</button>
                        <button onClick={() => { setDeletingId(v.visitor_id); setDeletingUuid(v.visitor_uuid); setShowDeleteConfirm(true); }} className="text-red-600 hover:text-red-800 text-sm font-medium">Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden space-y-3">
              {visitors.map(v => (
                <div key={v.visitor_id} className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <div className="font-mono text-sm font-bold text-gray-900">{v.visitor_uuid}</div>
                      <div className="text-sm text-gray-600">{v.user_name}</div>
                      <div className={`text-xs font-medium mt-1 ${v.visitor_type === 'Registered' ? 'text-green-700' : 'text-gray-600'}`}>{v.visitor_type}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs font-medium text-gray-600">Last Active</div>
                      <div className="text-sm font-bold text-gray-900">{fmtShort(v.last_seen_at)}</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-2 mb-3">
                    <div><div className="text-xs text-gray-500">Sessions</div><div className="text-sm font-bold">{v.sessions}</div></div>
                    <div><div className="text-xs text-gray-500">Page Views</div><div className="text-sm font-bold">{v.page_views}</div></div>
                    <div><div className="text-xs text-gray-500">Product Views</div><div className="text-sm font-bold">{v.product_views}</div></div>
                    <div><div className="text-xs text-gray-500">Basket Adds</div><div className="text-sm font-bold">{v.basket_adds}</div></div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => openDetail(v)} className="flex-1 px-3 py-2 bg-blue-600 text-white rounded text-sm font-medium hover:bg-blue-700">View Details</button>
                    <button onClick={() => { setDeletingId(v.visitor_id); setDeletingUuid(v.visitor_uuid); setShowDeleteConfirm(true); }} className="px-3 py-2 bg-red-600 text-white rounded text-sm font-medium hover:bg-red-700">Delete</button>
                  </div>
                </div>
              ))}
            </div>

{/* Pagination */}
            <div className="flex items-center justify-between mt-6">
              <div className="flex items-center gap-3">
                <div className="text-sm text-gray-600">
                  Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, pagination.total_records)} of {pagination.total_records} visitors
                </div>
                <select value={limit} onChange={(e) => { setLimit(Number(e.target.value)); setPage(1) }} className="text-sm border border-gray-300 rounded px-2 py-1">
                  {[10,25,50,100].map(n => <option key={n} value={n}>{n} / page</option>)}
                </select>
              </div>
              <div className="flex gap-1">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} className="px-3 py-1 rounded text-sm border bg-white text-gray-700 border-gray-300 hover:bg-gray-50 disabled:opacity-40">Prev</button>
                {Array.from({ length: pagination.total_pages }, (_, i) => i + 1).map(pg => (
                  <button key={pg} onClick={() => setPage(pg)} className={`w-8 h-8 rounded text-sm border ${pg === page ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}>{pg}</button>
                ))}
                <button onClick={() => setPage(p => Math.min(pagination.total_pages, p + 1))} disabled={page >= pagination.total_pages} className="px-3 py-1 rounded text-sm border bg-white text-gray-700 border-gray-300 hover:bg-gray-50 disabled:opacity-40">Next</button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Delete confirm modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-lg">
            <h2 className="text-xl font-bold text-gray-900 mb-2">Delete Visitor History</h2>
            <p className="text-gray-600 mb-6">Permanently delete all analytics records for visitor <strong>{deletingUuid}</strong>? This cannot be undone. Marketplace user accounts and orders are not affected.</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setShowDeleteConfirm(false)} className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300">Cancel</button>
              <button onClick={deleteVisitor} disabled={deletingId === null} className={`px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50`} >Delete</button>
            </div>
          </div>
        </div>
      )}
  
        {/* Visitor Detail Modal */}
        {detailOpen && detailVisitor && (
          <div className="fixed inset-0 z-[60] overflow-y-hidden overflow-x-hidden md:inset-0">
            <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm" onClick={() => setDetailOpen(false)} />
            <div className="fixed top-10 left-4 right-4 bottom-10 md:top-10 md:left-1/2 md:right-1/2 md:bottom-10 md:-translate-x-1/2 md:translate-y-0 md:w-full lg:w-[70vw] xl:w-[800px] z-50 bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center bg-gray-50">
                <h2 className="text-lg font-bold text-gray-900">Visitor Details</h2>
                <button onClick={() => setDetailOpen(false)} className="text-gray-400 hover:text-gray-600"><i className="fas fa-times text-xl"></i></button>
              </div>
              <div className="flex-1 overflow-y-auto p-6">
                {/* Visitor Overview */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
                    <p className="text-xs font-medium text-gray-500 uppercase">Visitor ID</p>
                    <p className="font-mono text-sm font-bold text-gray-900">{detailVisitor.visitor_uuid}</p>
                  </div>
                  <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
                    <p className="text-xs font-medium text-gray-500 uppercase">User</p>
                    <p className="text-sm font-bold text-gray-900">{detailVisitor.user_name}</p>
                    <p className="text-xs text-gray-500 mt-1">{detailVisitor.user_id ? 'Account ID: ' + detailVisitor.user_id : 'Not registered'}</p>
                  </div>
                  <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
                    <p className="text-xs font-medium text-gray-500 uppercase">Type</p>
                    <p className={`text-sm font-bold ${detailVisitor.visitor_type === 'Registered' ? 'text-green-600' : 'text-gray-600'}`}>{detailVisitor.visitor_type}</p>
                  </div>
                  <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
                    <p className="text-xs font-medium text-gray-500 uppercase">First Seen</p>
                    <p className="text-sm font-bold text-gray-900">{fmtDate(detailVisitor.first_seen_at)}</p>
                  </div>
                  <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
                    <p className="text-xs font-medium text-gray-500 uppercase">Last Active</p>
                    <p className="text-sm font-bold text-gray-900">{fmtDate(detailVisitor.last_seen_at)}</p>
                  </div>
                  <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
                    <p className="text-xs font-medium text-gray-500 uppercase">Sessions</p>
                    <p className="text-sm font-bold text-gray-900">{detailSummary?.sessions ?? 0}</p>
                  </div>
                  <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
                    <p className="text-xs font-medium text-gray-500 uppercase">Total Events</p>
                    <p className="text-sm font-bold text-gray-900">{detailSummary?.total_events ?? 0}</p>
                  </div>
                </div>

                {/* Activity Timeline */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-base font-bold text-gray-900">Activity Timeline</h3>
                    <button
                      onClick={() => loadDetailEvents(1)}
                      className="px-3 py-1.5 bg-white border border-gray-300 rounded text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-1"
                    >
                      <i className="fas fa-refresh"></i> Refresh
                    </button>
                  </div>
                  <div className="space-y-3">
                    {events.length === 0 ? (
                      <p className="text-gray-500 text-sm italic">No activity recorded for this visitor in the selected date range.</p>
                    ) : (
                      events.map(ev => (
                        <div key={ev.id} className="flex gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100">
                          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-xs font-bold">
                            {formatEventInitials(ev.event_type)}
                          </div>
                          <div className="flex-1">
                            <div className="text-sm font-medium text-gray-900 leading-tight mb-1">
                              {formatEventLabel(ev.event_type)}
                            </div>
                            {ev.product_name && (
                              <div className="text-gray-600 text-xs ml-1 truncate">
                                {ev.product_name}
                              </div>
                            )}
                            {ev.event_data && ev.event_data.quantity && (
                              <div className="text-gray-600 text-xs ml-1">
                                Quantity: {ev.event_data.quantity}
                              </div>
                            )}
                            {ev.event_data && ev.event_data.sizes && (
                              <div className="text-gray-600 text-xs ml-1">
                                Size{Object.keys(ev.event_data.sizes).length > 1 ? 's' : ''}: {formatSizes(ev.event_data.sizes)}
                              </div>
                            )}
                            <p className="text-xs text-gray-500 mt-1">
                              {fmtDate(ev.created_at)} • {ev.page_path || '-'}
                            </p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  {eventsPagination.total_pages > 1 && (
                    <div className="mt-4 flex justify-between items-center text-sm">
                      <span className="text-gray-600">Page {eventsPagination.page} of {eventsPagination.total_pages}</span>
                      <div className="flex gap-1">
                        {Array.from({ length: eventsPagination.total_pages }, (_, i) => i + 1).map(pg => (
                          <button key={pg} onClick={() => loadDetailEvents(pg)} className={`w-7 h-7 rounded text-xs border ${pg === eventsPagination.page ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}>{pg}</button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Product Interaction History */}
                <div className="mb-6">
                  <h3 className="text-base font-bold text-gray-900 mb-3">Product Interactions</h3>
                  {detailProducts.length === 0 ? (
                    <p className="text-gray-500 text-sm italic">No product interactions.</p>
                  ) : (
                    <div className="space-y-2">
                      {detailProducts.map(p => (
                        <div key={`${p.product_id}-${p.listing_type}`} className="flex gap-3 p-3 bg-white rounded-lg border border-gray-200">
                          <div className="flex-shrink-0 w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center text-gray-500 text-sm">
                            <i className="fas fa-box"></i>
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">{p.product_name}</p>
                            <p className="text-xs text-gray-500">{p.listing_number !== null && p.listing_number !== undefined && p.listing_number !== '' ? p.listing_number : ''} {p.category_name ? `• ${p.category_name}` : ''}</p>
                            <div className="flex gap-3 mt-2 text-xs">
                              <span className="px-2 py-0.5 bg-blue-50 text-blue-600 rounded">Views: {p.views}</span>
                              <span className="px-2 py-0.5 bg-orange-50 text-orange-600 rounded">Basket: {p.basket_adds}</span>
                              <span className="px-2 py-0.5 bg-green-50 text-green-600 rounded">Saves: {p.saves}</span>
                              <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded">Last: {fmtShort(p.last_interaction)}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Sessions */}
                <div className="mb-6">
                  <h3 className="text-base font-bold text-gray-900 mb-3">Sessions</h3>
                  {detailSessions.length === 0 ? (
                    <p className="text-gray-500 text-sm italic">No sessions recorded.</p>
                  ) : (
                    <div className="space-y-2">
                      {detailSessions.map(s => (
                        <button key={s.id} onClick={() => setSelectedSessionId(s.id)} className={`w-full flex items-center justify-between p-3 rounded-lg border text-left hover:bg-blue-50 ${selectedSessionId === s.id ? 'border-blue-200 bg-blue-50' : 'border-gray-100 hover:border-gray-200'}`}>
                          <div>
                            <div className="text-sm font-medium text-gray-900">{s.session_uuid}</div>
                            <div className="text-xs text-gray-500">{fmtDate(s.started_at)} → {s.ended_at ? fmtDate(s.ended_at) : 'Active'}</div>
                          </div>
                          <div className="text-xs text-gray-600">{s.page_view_count} pages • {s.event_count} events</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
                <button onClick={handleDeleteHistory} className="px-4 py-2 text-red-600 hover:text-red-800 text-sm font-medium">Delete History</button>
                <button onClick={() => setDetailOpen(false)} className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 text-sm font-medium">Close</button>
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }

